import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { fetchCampaignDonors } from '@/lib/campaign-reports'
import type { CampaignDonorsResponse } from '@/types/campaign-report'

interface UseCampaignDonorsOptions {
  campaignId: number | null
  page: number
  perPage: number
  dateFrom?: string | null
  dateTo?: string | null
  enabled?: boolean
}

export function useCampaignDonors({
  campaignId,
  page,
  perPage,
  dateFrom,
  dateTo,
  enabled = true,
}: UseCampaignDonorsOptions) {
  const { token } = useAuth()
  const [data, setData] = useState<CampaignDonorsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!enabled) {
      setData(null)
      setIsLoading(false)
      setError(null)
      return
    }

    if (!campaignId) {
      setData(null)
      setIsLoading(false)
      setError(null)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetchCampaignDonors(campaignId, { page, perPage, dateFrom, dateTo }, token)
      setData(response)
    } catch (err) {
      setData(null)
      setError(
        err instanceof Error
          ? err.message
          : 'Nao foi possivel carregar os compradores da campanha.'
      )
    } finally {
      setIsLoading(false)
    }
  }, [campaignId, enabled, page, perPage, dateFrom, dateTo, token])

  useEffect(() => {
    void load()
  }, [load])

  return {
    data,
    isLoading,
    error,
    refetch: load,
  }
}
